import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Tag, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/axios';
import { EtiquetaChip } from './EtiquetaChip';
import { MAX_ETIQUETAS, normEtiqueta, mismaEtiqueta } from './vigilanciaUtils';

const sumar = (lista, t) => {
  const e = normEtiqueta(t);
  if (!e || lista.length >= MAX_ETIQUETAS || lista.some((x) => mismaEtiqueta(x, e))) return lista;
  return [...lista, e];
};

export const EtiquetasDialog = ({ vig, sugerencias = [], onOpenChange, onSaved }) => {
  const [lista, setLista] = useState([]);
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (vig) { setLista(vig.etiquetas || []); setTexto(''); } }, [vig]);

  const lleno = lista.length >= MAX_ETIQUETAS;
  const libres = sugerencias.filter((s) => !lista.some((x) => mismaEtiqueta(x, s)));
  const agregar = (t) => { setLista((l) => sumar(l, t)); setTexto(''); };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/vigilancia/cuentas/${vig.id}`, { etiquetas: sumar(lista, texto) });
      toast.success('Carteles guardados');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudieron guardar los carteles');
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open={!!vig} onOpenChange={onOpenChange}>
      <DialogContent className="vig-scope max-w-md" data-testid="vig-etiquetas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag size={18} className="text-teal-700" /> Carteles de {vig?.nombre || 'la cuenta'}</DialogTitle>
          <DialogDescription>Hasta {MAX_ETIQUETAS} textos para identificarla y filtrarla. Tocá la X para quitar uno.</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-[2rem] flex-wrap gap-1.5" data-testid="vig-etiquetas-actuales">
          {lista.map((t, i) => (
            <EtiquetaChip key={t} texto={t} testid={`vig-etiquetas-actual-${i}`} onRemove={() => setLista((l) => l.filter((x) => x !== t))} />
          ))}
          {!lista.length && <p className="text-xs text-slate-400">Sin carteles todavía.</p>}
        </div>
        <div className="flex gap-2">
          <Input
            value={texto} maxLength={30} disabled={lleno} data-testid="vig-etiquetas-input"
            placeholder={lleno ? `Máximo ${MAX_ETIQUETAS} carteles` : 'Ej: Proveedor, Revisar lunes…'}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregar(texto); } }}
          />
          <Button type="button" variant="outline" onClick={() => agregar(texto)} disabled={lleno || !normEtiqueta(texto)} data-testid="vig-etiquetas-agregar-btn">
            <Plus size={15} className="mr-1" /> Agregar
          </Button>
        </div>
        {!lleno && libres.length > 0 && (
          <div className="space-y-1.5" data-testid="vig-etiquetas-sugerencias">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Carteles en uso</p>
            <div className="flex flex-wrap gap-1.5">
              {libres.map((s, i) => <EtiquetaChip key={s} texto={s} testid={`vig-etiquetas-sugerencia-${i}`} onClick={() => agregar(s)} />)}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando} className="bg-teal-700 hover:bg-teal-800" data-testid="vig-etiquetas-guardar-btn">
            {guardando && <Loader2 size={16} className="mr-1.5 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EtiquetasDialog;
